import { __rest, __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import React__default, { createContext, useState, useContext, Fragment, useRef, useEffect, useCallback, useMemo, Children } from 'react';
import { Button } from '../Components/ui/button.js';
import '../Components/ui/command.js';
import '../Components/ui/dropdown-menu.js';
import { HoverCardTrigger, HoverCard, HoverCardContent } from '../Components/ui/hover-card.js';
import { InputGroupTextarea, InputGroup, InputGroupButton } from '../Components/ui/input-group.js';
import '../Components/ui/select.js';
import { cn } from '../lib/utils.js';
import { XIcon, PaperclipIcon, MicIcon } from 'lucide-react';
import { nanoid } from 'nanoid';

const PromptInputController = createContext(null);
const ProviderAttachmentsContext = createContext(null);
// Optional variants (do NOT throw). Useful for dual-mode components.
const useOptionalPromptInputController = () => useContext(PromptInputController);
const useOptionalProviderAttachments = () => useContext(ProviderAttachmentsContext);
// ============================================================================
// Component Context & Hooks
// ============================================================================
const LocalAttachmentsContext = createContext(null);
const usePromptInputAttachments = () => {
    // Dual-mode: prefer provider if present, otherwise use local
    const provider = useOptionalProviderAttachments();
    const local = useContext(LocalAttachmentsContext);
    const context = provider !== null && provider !== void 0 ? provider : local;
    if (!context) {
        throw new Error('usePromptInputAttachments must be used within a PromptInput or PromptInputProvider');
    }
    return context;
};
function PromptInputAttachment(_a) {
    var _b;
    var { data, className } = _a, props = __rest(_a, ["data", "className"]);
    const attachments = usePromptInputAttachments();
    const filename = data.filename || '';
    const mediaType = ((_b = data.mediaType) === null || _b === void 0 ? void 0 : _b.startsWith('image/')) && data.url ? 'image' : 'file';
    const isImage = mediaType === 'image';
    // Image attachment: 84x84 square with close button in top right, hover to see larger preview
    if (isImage) {
        return (jsx("div", Object.assign({ style: { padding: 6, alignSelf: 'flex-start' } }, { children: jsxs(PromptInputHoverCard, Object.assign({ openDelay: 200 }, { children: [jsx(HoverCardTrigger, Object.assign({ asChild: true }, { children: jsxs("div", Object.assign({ className: cn('tw:relative tw:flex tw:justify-end tw:items-start tw:shrink-0 tw:rounded-lg tw:border tw:border-border tw:overflow-hidden tw:bg-background tw:cursor-pointer', className), style: { width: 84, height: 84, padding: 6 } }, props, { children: [jsx("img", { alt: filename || 'attachment', src: data.url, style: {
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 8,
                                    } }), jsxs("button", Object.assign({ "aria-label": "Remove attachment", onClick: (e) => {
                                        e.stopPropagation();
                                        attachments.remove(data.id);
                                    }, onPointerEnter: (e) => e.stopPropagation(), onPointerLeave: (e) => e.stopPropagation(), onMouseEnter: (e) => e.stopPropagation(), onMouseLeave: (e) => e.stopPropagation(), type: "button", style: {
                                        position: 'relative',
                                        zIndex: 10,
                                        width: 20,
                                        height: 20,
                                        minWidth: 20,
                                        cursor: 'pointer',
                                        borderRadius: '50%',
                                        padding: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    } }, { children: [jsx(XIcon, { style: { width: 12, height: 12 } }), jsx("span", Object.assign({ className: "tw:sr-only" }, { children: "Remove" }))] }))] }), data.id) })), jsx(PromptInputHoverCardContent, Object.assign({ className: "tw:w-auto tw:p-2", side: "top", sideOffset: 8, collisionPadding: 16, avoidCollisions: true }, { children: jsx("div", Object.assign({ className: "tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md" }, { children: jsx("img", { alt: filename || 'attachment preview', src: data.url, style: { maxHeight: 280, maxWidth: 280, objectFit: 'contain' } }) })) }))] })) })));
    }
    // Non-image attachment: keep original pill style with hover card
    const attachmentLabel = filename || 'Attachment';
    return (jsxs(PromptInputHoverCard, { children: [jsx(HoverCardTrigger, Object.assign({ asChild: true }, { children: jsxs("div", Object.assign({ className: cn('tw:group tw:relative tw:flex tw:h-8 tw:cursor-default tw:select-none tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-border tw:px-1.5 tw:font-medium tw:text-sm tw:transition-all tw:hover:bg-accent tw:hover:text-accent-foreground tw:dark:hover:bg-accent/50', className) }, props, { children: [jsxs("div", Object.assign({ className: "tw:relative tw:size-5 tw:shrink-0" }, { children: [jsx("div", Object.assign({ className: "tw:absolute tw:inset-0 tw:flex tw:size-5 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:bg-background tw:transition-opacity tw:group-hover:opacity-0" }, { children: jsx("div", Object.assign({ className: "tw:flex tw:size-5 tw:items-center tw:justify-center tw:text-muted-foreground" }, { children: jsx(PaperclipIcon, { className: "tw:size-3" }) })) })), jsxs(Button, Object.assign({ "aria-label": "Remove attachment", className: "tw:absolute tw:inset-0 tw:size-5 tw:cursor-pointer tw:rounded tw:p-0 tw:opacity-0 tw:transition-opacity tw:group-hover:pointer-events-auto tw:group-hover:opacity-100 tw:[&>svg]:size-2.5", onClick: (e) => {
                                        e.stopPropagation();
                                        attachments.remove(data.id);
                                    }, type: "button", variant: "ghost" }, { children: [jsx(XIcon, {}), jsx("span", Object.assign({ className: "tw:sr-only" }, { children: "Remove" }))] }))] })), jsx("span", Object.assign({ className: "tw:flex-1 tw:truncate" }, { children: attachmentLabel }))] }), data.id) })), jsx(PromptInputHoverCardContent, Object.assign({ className: "tw:w-auto tw:p-2" }, { children: jsx("div", Object.assign({ className: "tw:flex tw:items-center tw:gap-2.5" }, { children: jsxs("div", Object.assign({ className: "tw:min-w-0 tw:flex-1 tw:space-y-1 tw:px-0.5" }, { children: [jsx("h4", Object.assign({ className: "tw:truncate tw:font-semibold tw:text-sm tw:leading-none" }, { children: attachmentLabel })), data.mediaType && (jsx("p", Object.assign({ className: "tw:truncate tw:font-mono tw:text-muted-foreground tw:text-xs" }, { children: data.mediaType })))] })) })) }))] }));
}
function PromptInputAttachments({ children }) {
    const attachments = usePromptInputAttachments();
    if (!attachments.files.length) {
        return null;
    }
    return attachments.files.map((file) => jsx(Fragment, { children: children(file) }, file.id));
}
const PromptInput = (_a) => {
    var { className, accept, multiple, globalDrop, syncHiddenInput, maxFiles, maxFileSize, onError, onSubmit, children } = _a, props = __rest(_a, ["className", "accept", "multiple", "globalDrop", "syncHiddenInput", "maxFiles", "maxFileSize", "onError", "onSubmit", "children"]);
    // Try to use a provider controller if present
    const controller = useOptionalPromptInputController();
    const usingProvider = !!controller;
    // Refs
    const inputRef = useRef(null);
    const anchorRef = useRef(null);
    const formRef = useRef(null);
    // Find nearest form to scope drag & drop
    useEffect(() => {
        var _a;
        const root = (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.closest('form');
        if (root instanceof HTMLFormElement) {
            formRef.current = root;
        }
    }, []);
    // ----- Local attachments (only used when no provider)
    const [items, setItems] = useState([]);
    const files = usingProvider ? controller.attachments.files : items;
    const openFileDialogLocal = useCallback(() => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    const matchesAccept = useCallback((f) => {
        if (!accept || accept.trim() === '') {
            return true;
        }
        if (accept.includes('image/*')) {
            return f.type.startsWith('image/');
        }
        // NOTE: keep simple; expand as needed
        return true;
    }, [accept]);
    const addLocal = useCallback((fileList) => {
        const incoming = Array.from(fileList);
        const accepted = incoming.filter((f) => matchesAccept(f));
        if (incoming.length && accepted.length === 0) {
            onError === null || onError === void 0 ? void 0 : onError({
                code: 'accept',
                message: 'No files match the accepted types.',
            });
            return;
        }
        const withinSize = (f) => (maxFileSize ? f.size <= maxFileSize : true);
        const sized = accepted.filter(withinSize);
        if (accepted.length > 0 && sized.length === 0) {
            onError === null || onError === void 0 ? void 0 : onError({
                code: 'max_file_size',
                message: 'All files exceed the maximum size.',
            });
            return;
        }
        setItems((prev) => {
            const capacity = typeof maxFiles === 'number' ? Math.max(0, maxFiles - prev.length) : undefined;
            const capped = typeof capacity === 'number' ? sized.slice(0, capacity) : sized;
            if (typeof capacity === 'number' && sized.length > capacity) {
                onError === null || onError === void 0 ? void 0 : onError({
                    code: 'max_files',
                    message: 'Too many files. Some were not added.',
                });
            }
            const next = [];
            for (const file of capped) {
                next.push({
                    id: nanoid(),
                    type: 'file',
                    url: URL.createObjectURL(file),
                    mediaType: file.type,
                    filename: file.name,
                });
            }
            return prev.concat(next);
        });
    }, [matchesAccept, maxFiles, maxFileSize, onError]);
    const add = usingProvider ? (files) => controller.attachments.add(files) : addLocal;
    const remove = usingProvider
        ? (id) => controller.attachments.remove(id)
        : (id) => setItems((prev) => {
            const found = prev.find((file) => file.id === id);
            if (found === null || found === void 0 ? void 0 : found.url) {
                URL.revokeObjectURL(found.url);
            }
            return prev.filter((file) => file.id !== id);
        });
    const clear = usingProvider
        ? () => controller.attachments.clear()
        : () => setItems((prev) => {
            for (const file of prev) {
                if (file.url) {
                    URL.revokeObjectURL(file.url);
                }
            }
            return [];
        });
    const openFileDialog = usingProvider ? () => controller.attachments.openFileDialog() : openFileDialogLocal;
    // Let provider know about our hidden file input so external menus can call openFileDialog()
    useEffect(() => {
        if (!usingProvider)
            return;
        controller.__registerFileInput(inputRef, () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); });
    }, [usingProvider, controller]);
    // Note: File input cannot be programmatically set for security reasons
    // The syncHiddenInput prop is no longer functional
    useEffect(() => {
        if (syncHiddenInput && inputRef.current && files.length === 0) {
            inputRef.current.value = '';
        }
    }, [files, syncHiddenInput]);
    // Attach drop handlers on nearest form and document (opt-in)
    useEffect(() => {
        const form = formRef.current;
        if (!form)
            return;
        const onDragOver = (e) => {
            var _a, _b;
            if ((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.types) === null || _b === void 0 ? void 0 : _b.includes('Files')) {
                e.preventDefault();
            }
        };
        const onDrop = (e) => {
            var _a, _b, _c;
            if ((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.types) === null || _b === void 0 ? void 0 : _b.includes('Files')) {
                e.preventDefault();
            }
            if (((_c = e.dataTransfer) === null || _c === void 0 ? void 0 : _c.files) && e.dataTransfer.files.length > 0) {
                add(e.dataTransfer.files);
            }
        };
        form.addEventListener('dragover', onDragOver);
        form.addEventListener('drop', onDrop);
        return () => {
            form.removeEventListener('dragover', onDragOver);
            form.removeEventListener('drop', onDrop);
        };
    }, [add]);
    useEffect(() => {
        if (!globalDrop)
            return;
        const onDragOver = (e) => {
            var _a, _b;
            if ((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.types) === null || _b === void 0 ? void 0 : _b.includes('Files')) {
                e.preventDefault();
            }
        };
        const onDrop = (e) => {
            var _a, _b, _c;
            if ((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.types) === null || _b === void 0 ? void 0 : _b.includes('Files')) {
                e.preventDefault();
            }
            if (((_c = e.dataTransfer) === null || _c === void 0 ? void 0 : _c.files) && e.dataTransfer.files.length > 0) {
                add(e.dataTransfer.files);
            }
        };
        document.addEventListener('dragover', onDragOver);
        document.addEventListener('drop', onDrop);
        return () => {
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('drop', onDrop);
        };
    }, [add, globalDrop]);
    useEffect(() => () => {
        if (!usingProvider) {
            for (const f of files) {
                if (f.url)
                    URL.revokeObjectURL(f.url);
            }
        }
    }, [usingProvider, files]);
    const handleChange = (event) => {
        if (event.currentTarget.files) {
            add(event.currentTarget.files);
        }
    };
    const convertBlobUrlToDataUrl = (url) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield fetch(url);
        const blob = yield response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    });
    const ctx = useMemo(() => ({
        files: files.map((item) => (Object.assign(Object.assign({}, item), { id: item.id }))),
        add,
        remove,
        clear,
        openFileDialog,
        fileInputRef: inputRef,
    }), [files, add, remove, clear, openFileDialog]);
    const handleSubmit = (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const text = usingProvider
            ? controller.textInput.value
            : (() => {
                const formData = new FormData(form);
                return formData.get('message') || '';
            })();
        // Reset form immediately after capturing text to avoid race condition
        // where user input during async blob conversion would be lost
        if (!usingProvider) {
            form.reset();
        }
        // Convert blob URLs to data URLs asynchronously
        Promise.all(files.map((_a) => __awaiter(void 0, void 0, void 0, function* () {
            var item = __rest(_a, ["id"]);
            if (item.url && item.url.startsWith('blob:')) {
                return Object.assign(Object.assign({}, item), { url: yield convertBlobUrlToDataUrl(item.url) });
            }
            return item;
        }))).then((convertedFiles) => {
            try {
                const result = onSubmit({ text, files: convertedFiles }, event);
                // Handle both sync and async onSubmit
                if (result instanceof Promise) {
                    result
                        .then(() => {
                        clear();
                        if (usingProvider) {
                            controller.textInput.clear();
                        }
                    })
                        .catch(() => {
                        // Don't clear on error - user may want to retry
                    });
                }
                else {
                    // Sync function completed without throwing, clear attachments
                    clear();
                    if (usingProvider) {
                        controller.textInput.clear();
                    }
                }
            }
            catch (error) {
                // Don't clear on error - user may want to retry
            }
        });
    };
    // Render with or without local provider
    const inner = (jsxs(Fragment$1, { children: [jsx("span", { "aria-hidden": "true", className: "tw:hidden", ref: anchorRef }), jsx("input", { accept: accept, "aria-label": "Upload files", className: "tw:hidden", multiple: multiple, onChange: handleChange, ref: inputRef, title: "Upload files", type: "file" }), jsx("form", Object.assign({ className: cn('tw:w-full', className), onSubmit: handleSubmit }, props, { children: jsx(InputGroup, { children: children }) }))] }));
    return usingProvider ? (inner) : (jsx(LocalAttachmentsContext.Provider, Object.assign({ value: ctx }, { children: inner })));
};
const PromptInputBody = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (jsx("div", Object.assign({ className: cn('tw:contents', className) }, props)));
};
const PromptInputTextarea = React__default.forwardRef((_a, ref) => {
    var { onChange, className, placeholder = 'What would you like to know?' } = _a, props = __rest(_a, ["onChange", "className", "placeholder"]);
    const controller = useOptionalPromptInputController();
    const attachments = usePromptInputAttachments();
    const [isComposing, setIsComposing] = useState(false);
    const handleKeyDown = (e) => {
        var _a;
        if (e.key === 'Enter') {
            if (isComposing || e.nativeEvent.isComposing) {
                return;
            }
            if (e.shiftKey) {
                return;
            }
            e.preventDefault();
            (_a = e.currentTarget.form) === null || _a === void 0 ? void 0 : _a.requestSubmit();
        }
        // Remove last attachment when Backspace is pressed and textarea is empty
        if (e.key === 'Backspace' && e.currentTarget.value === '' && attachments.files.length > 0) {
            e.preventDefault();
            const lastAttachment = attachments.files.at(-1);
            if (lastAttachment) {
                attachments.remove(lastAttachment.id);
            }
        }
    };
    const handlePaste = (event) => {
        var _a;
        const items = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.items;
        if (!items) {
            return;
        }
        const files = [];
        for (const item of items) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }
        if (files.length > 0) {
            event.preventDefault();
            attachments.add(files);
        }
    };
    const controlledProps = controller
        ? {
            value: controller.textInput.value,
            onChange: (e) => {
                controller.textInput.setInput(e.currentTarget.value);
                onChange === null || onChange === void 0 ? void 0 : onChange(e);
            },
        }
        : {
            onChange,
        };
    return (jsx(InputGroupTextarea, Object.assign({ ref: ref, className: cn('tw:field-sizing-content tw:max-h-48 tw:min-h-16', className), name: "message", onCompositionEnd: () => setIsComposing(false), onCompositionStart: () => setIsComposing(true), onKeyDown: handleKeyDown, onPaste: handlePaste, placeholder: placeholder }, props, controlledProps)));
});
PromptInputTextarea.displayName = 'PromptInputTextarea';
const PromptInputButton = (_a) => {
    var { variant = 'ghost', className, size } = _a, props = __rest(_a, ["variant", "className", "size"]);
    const newSize = size !== null && size !== void 0 ? size : (Children.count(props.children) > 1 ? 'sm' : 'icon-sm');
    return jsx(InputGroupButton, Object.assign({ className: cn(className), size: newSize, type: "button", variant: variant }, props));
};
const PromptInputSpeechButton = (_a) => {
    var { className, textareaRef, onTranscriptionChange, onListeningChange, children } = _a, props = __rest(_a, ["className", "textareaRef", "onTranscriptionChange", "onListeningChange", "children"]);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const recognitionRef = useRef(null);
    useEffect(() => {
        if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const speechRecognition = new SpeechRecognition();
            speechRecognition.continuous = true;
            speechRecognition.interimResults = true;
            speechRecognition.lang = 'en-US';
            speechRecognition.onstart = () => {
                setIsListening(true);
                onListeningChange === null || onListeningChange === void 0 ? void 0 : onListeningChange(true);
            };
            speechRecognition.onend = () => {
                setIsListening(false);
                onListeningChange === null || onListeningChange === void 0 ? void 0 : onListeningChange(false);
            };
            speechRecognition.onresult = (event) => {
                var _a, _b;
                let finalTranscript = '';
                const results = Array.from(event.results);
                for (const result of results) {
                    if (result.isFinal) {
                        finalTranscript += (_b = (_a = result[0]) === null || _a === void 0 ? void 0 : _a.transcript) !== null && _b !== void 0 ? _b : '';
                    }
                }
                if (finalTranscript && (textareaRef === null || textareaRef === void 0 ? void 0 : textareaRef.current)) {
                    const textarea = textareaRef.current;
                    const currentValue = textarea.value;
                    const newValue = currentValue + (currentValue ? ' ' : '') + finalTranscript;
                    textarea.value = newValue;
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                    onTranscriptionChange === null || onTranscriptionChange === void 0 ? void 0 : onTranscriptionChange(newValue);
                }
            };
            speechRecognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                onListeningChange === null || onListeningChange === void 0 ? void 0 : onListeningChange(false);
            };
            recognitionRef.current = speechRecognition;
            setRecognition(speechRecognition);
        }
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, [textareaRef, onTranscriptionChange, onListeningChange]);
    const toggleListening = useCallback(() => {
        if (!recognition) {
            return;
        }
        if (isListening) {
            recognition.stop();
        }
        else {
            recognition.start();
        }
    }, [recognition, isListening]);
    const renderedChildren = typeof children === 'function' ? children(isListening) : (children !== null && children !== void 0 ? children : jsx(MicIcon, { className: "tw:size-4" }));
    return (jsx(PromptInputButton, Object.assign({ className: cn('tw:relative tw:transition-all tw:duration-200', isListening && 'tw:animate-pulse tw:bg-accent tw:text-accent-foreground', className), disabled: !recognition, onClick: toggleListening }, props, { children: renderedChildren })));
};
const PromptInputHoverCard = (_a) => {
    var { openDelay = 0, closeDelay = 0 } = _a, props = __rest(_a, ["openDelay", "closeDelay"]);
    return (jsx(HoverCard, Object.assign({ closeDelay: closeDelay, openDelay: openDelay }, props)));
};
const PromptInputHoverCardContent = (_a) => {
    var { align = 'start' } = _a, props = __rest(_a, ["align"]);
    return (jsx(HoverCardContent, Object.assign({ align: align }, props)));
};

export { PromptInput, PromptInputAttachment, PromptInputAttachments, PromptInputBody, PromptInputButton, PromptInputHoverCard, PromptInputHoverCardContent, PromptInputSpeechButton, PromptInputTextarea, usePromptInputAttachments };
//# sourceMappingURL=prompt-input.js.map
